import { z } from "zod";
import { defineEndpoint, idParamsSchema } from "./endpoint.js";
import { paisa } from "./money.js";
import { businessPhoneSchema } from "./settings.js";
/** Suppliers, supplier payments and payables (docs/specs/purchasing.md, P-01, P-04). */
export const SUPPLIER_STATUSES = ["active", "archived"];
export const supplierStatusSchema = z.enum(SUPPLIER_STATUSES);
const text = (max) => z.string().trim().max(max);
const version = z.int().min(1);
export const supplierSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    contactName: z.string(),
    /** E.164. */
    phone: z.string().nullable(),
    email: z.string(),
    address: z.string(),
    note: z.string(),
    /** What was owed before the system (positive: the business owes the supplier). */
    openingBalancePaisa: paisa(),
    /** Opening + receipts − returns − payments; negative is an advance. */
    balancePaisa: paisa(),
    status: supplierStatusSchema,
    version: z.int(),
    createdAt: z.iso.datetime(),
});
export const supplierListQuerySchema = z.object({
    q: z.string().trim().max(100).optional(),
    status: supplierStatusSchema.optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    cursor: z.uuid().optional(),
});
export const supplierListSchema = z.object({
    data: z.array(supplierSchema),
    nextCursor: z.uuid().nullable(),
});
const supplierFields = {
    name: z.string().trim().min(1).max(200),
    contactName: text(200).optional(),
    phone: businessPhoneSchema.nullable().optional(),
    email: z.union([z.literal(""), z.email().max(200)]).optional(),
    address: text(500).optional(),
    note: text(500).optional(),
};
/** `POST /v1/suppliers`; the opening balance is set once, here. */
export const createSupplierRequestSchema = z
    .object({ ...supplierFields, openingBalancePaisa: paisa().default(0) })
    .strict();
export const updateSupplierRequestSchema = z
    .object({
    name: supplierFields.name.optional(),
    contactName: supplierFields.contactName,
    phone: supplierFields.phone,
    email: supplierFields.email,
    address: supplierFields.address,
    note: supplierFields.note,
    status: supplierStatusSchema.optional(),
    version,
})
    .strict();
// Payments -------------------------------------------------------------------------------------------------------
export const SUPPLIER_PAYMENT_METHODS = [
    "cash",
    "bank_transfer",
    "cheque",
    "bkash",
    "nagad",
    "rocket",
    "other",
];
export const supplierPaymentMethodSchema = z.enum(SUPPLIER_PAYMENT_METHODS);
export const supplierPaymentSchema = z.object({
    id: z.uuid(),
    number: z.string(),
    supplierId: z.uuid(),
    /** Positive: paid to the supplier; negative: a reversal of `reversalOfId`. */
    amountPaisa: paisa(),
    method: supplierPaymentMethodSchema,
    reference: z.string(),
    paidAt: z.iso.datetime(),
    note: z.string(),
    reversalOfId: z.uuid().nullable(),
    /** Set on a payment that has been reversed. */
    reversedById: z.uuid().nullable(),
    /** The payment account the money came from (A-01, B-54); null: not recorded against an account. */
    accountId: z.uuid().nullable(),
    createdBy: z.uuid().nullable(),
    createdAt: z.iso.datetime(),
});
export const supplierPaymentListQuerySchema = z.object({
    supplierId: z.uuid().optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    cursor: z.uuid().optional(),
});
export const supplierPaymentListSchema = z.object({
    data: z.array(supplierPaymentSchema),
    nextCursor: z.uuid().nullable(),
});
/** `POST /v1/supplier-payments`: partial payments are normal; more than owed leaves an advance. */
export const recordSupplierPaymentRequestSchema = z
    .object({
    supplierId: z.uuid(),
    amountPaisa: paisa().positive(),
    method: supplierPaymentMethodSchema,
    reference: text(100).default(""),
    paidAt: z.iso.datetime({ offset: true }).optional(),
    note: text(500).default(""),
    /** Pay from this account: a payment is recorded there too (B-54). */
    accountId: z.uuid().nullable().default(null),
})
    .strict();
export const reverseSupplierPaymentRequestSchema = z.object({ note: text(500).min(1) }).strict();
// Aging ----------------------------------------------------------------------------------------------------------
export const AGING_BUCKETS = ["current", "days31to60", "days61to90", "over90"];
/** Per supplier: what is owed, by the age of the receipts it comes from (payments settle the oldest first). */
export const payablesAgingSchema = z.object({
    asOf: z.iso.datetime(),
    data: z.array(z.object({
        supplierId: z.uuid(),
        supplierName: z.string(),
        balancePaisa: paisa(),
        /** 0–30, 31–60, 61–90 and over 90 days since the receipt. */
        current: paisa(),
        days31to60: paisa(),
        days61to90: paisa(),
        over90: paisa(),
        /** Paid more than owed. */
        advancePaisa: paisa(),
    })),
    totals: z.object({
        balancePaisa: paisa(),
        current: paisa(),
        days31to60: paisa(),
        days61to90: paisa(),
        over90: paisa(),
        advancePaisa: paisa(),
    }),
});
// Events -----------------------------------------------------------------------------------------------------------
export const supplierPaymentEventSchema = supplierPaymentSchema.omit({ reversedById: true });
// Endpoints ------------------------------------------------------------------------------------------------------
const tag = { tag: "purchasing" };
const view = { permission: "purchasing.view" };
const manage = { permission: "suppliers.manage" };
const pay = { permission: "supplier_payments.manage" };
export const listSuppliers = defineEndpoint({
    ...tag,
    name: "listSuppliers",
    method: "GET",
    path: "/v1/suppliers",
    access: view,
    summary: "Suppliers by name, with their payable balance",
    query: supplierListQuerySchema,
    status: 200,
    response: supplierListSchema,
});
export const createSupplier = defineEndpoint({
    ...tag,
    name: "createSupplier",
    method: "POST",
    path: "/v1/suppliers",
    access: manage,
    summary: "Add a supplier (with what is already owed to them)",
    body: createSupplierRequestSchema,
    status: 201,
    response: supplierSchema,
    errors: ["CONFLICT"],
});
export const getSupplier = defineEndpoint({
    ...tag,
    name: "getSupplier",
    method: "GET",
    path: "/v1/suppliers/{id}",
    access: view,
    summary: "One supplier with the payable balance",
    params: idParamsSchema,
    status: 200,
    response: supplierSchema,
});
export const updateSupplier = defineEndpoint({
    ...tag,
    name: "updateSupplier",
    method: "PATCH",
    path: "/v1/suppliers/{id}",
    access: manage,
    summary: "Change contact details, archive or unarchive",
    params: idParamsSchema,
    body: updateSupplierRequestSchema,
    status: 200,
    response: supplierSchema,
    errors: ["CONFLICT"],
});
export const listSupplierPayments = defineEndpoint({
    ...tag,
    name: "listSupplierPayments",
    method: "GET",
    path: "/v1/supplier-payments",
    access: view,
    summary: "Payments to suppliers, newest first",
    query: supplierPaymentListQuerySchema,
    status: 200,
    response: supplierPaymentListSchema,
});
export const recordSupplierPayment = defineEndpoint({
    ...tag,
    name: "recordSupplierPayment",
    method: "POST",
    path: "/v1/supplier-payments",
    access: pay,
    summary: "Record a (partial) payment to a supplier",
    body: recordSupplierPaymentRequestSchema,
    status: 201,
    response: supplierPaymentSchema,
    errors: ["SUPPLIER_ARCHIVED"],
    idempotent: true,
});
export const reverseSupplierPayment = defineEndpoint({
    ...tag,
    name: "reverseSupplierPayment",
    method: "POST",
    path: "/v1/supplier-payments/{id}/reverse",
    access: pay,
    summary: "Undo a payment entered by mistake (a reversing entry)",
    params: idParamsSchema,
    body: reverseSupplierPaymentRequestSchema,
    status: 201,
    response: supplierPaymentSchema,
    errors: ["ALREADY_REVERSED"],
    idempotent: true,
});
export const getPayablesAging = defineEndpoint({
    ...tag,
    name: "getPayablesAging",
    method: "GET",
    path: "/v1/payables/aging",
    access: view,
    summary: "What is owed to each supplier, by age",
    status: 200,
    response: payablesAgingSchema,
});
//# sourceMappingURL=suppliers.js.map